import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { Guild } from "@devour/client";
import classNames from "classnames";
import { toggleLoginOpen } from "@/redux/meta/metaActions";
import { ViewMode } from "@/components/goFrens/GoFrensTopLeaderboard";
import { getRankSuffix } from "@/utils/getRankSuffix";
import { useAsset } from "@/hooks/useAsset";
import { FiInfo, FiShield, FiTrendingUp } from "react-icons/fi";
import { IoMdPersonAdd } from "react-icons/io";
import { BiCommentError } from "react-icons/bi";
import { IoExitOutline } from "react-icons/io5";
import { HiOutlineSparkles } from "react-icons/hi";
import "./GuildHeaderCockpit.scss";

interface GuildHeaderCockpitProps {
    guild?: Guild | null;
    topGuilds?: any[];
    isLoading?: boolean;
    onOpenInvite?: () => void;
    onOpenAbout?: () => void;
    onLeaveGuild?: () => void;
    onBrowseGuilds?: () => void;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
}

export default function GuildHeaderCockpit({
    guild,
    topGuilds,
    isLoading,
    onOpenInvite,
    onOpenAbout,
    onLeaveGuild,
    onBrowseGuilds,
    viewMode,
    onViewModeChange,
}: GuildHeaderCockpitProps) {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [showInfo, setShowInfo] = useState(false);

    // Fetch guild icon if available
    const { data: guildIcon } = useAsset(guild?.icon);

    // Determine which state we're in
    const userState = !fullToken ? "ftue" : !guild ? "no-guild" : "member";

    // Get rank and points based on view mode
    const rank = guild ? viewMode === ViewMode.MONTHLY ? guild.monthlyRank : guild.rank : null;
    const points = guild ? viewMode === ViewMode.MONTHLY ? guild.monthlyPoints : guild.totalPoints : null;

    return (
        <div className={classNames("guild-header-cockpit", userState, { loading: isLoading })}>
            {/* Main Header Bar - Always visible, sticky */}
            <div className="guild-header-cockpit__bar">
                <div className="guild-header-cockpit__title-section">
                    <div className="guild-header-cockpit__icon">
                        <FiShield />
                    </div>
                    <div className="guild-header-cockpit__title">
                        <h2>{userState === "member" ? "My Guild" : "Guilds"}</h2>
                        <p className="tagline">Compete, climb, and win together</p>
                    </div>
                </div>

                <div className="guild-header-cockpit__controls">
                    {/* View Mode Toggle - Only show for members */}
                    {userState === "member" &&
            <div className="view-toggle">
                <button
                    className={classNames("toggle-btn", { active: viewMode === ViewMode.MONTHLY })}
                    onClick={() => onViewModeChange(ViewMode.MONTHLY)}
                >
                    Monthly
                </button>
                <button
                    className={classNames("toggle-btn", { active: viewMode === ViewMode.CUMULATIVE })}
                    onClick={() => onViewModeChange(ViewMode.CUMULATIVE)}
                >
                    All-Time
                </button>
            </div>
                    }

                    {/* Info Button */}
                    <button
                        className="info-btn"
                        onClick={() => setShowInfo(!showInfo)}
                        aria-label="Guild rules and information"
                    >
                        <FiInfo />
                    </button>
                </div>
            </div>

            {/* Status Bar - State-dependent content */}
            <div className="guild-header-cockpit__status">
                {userState === "ftue" &&
          <div className="status-content status-content--ftue">
              <div className="status-indicator">
                  <HiOutlineSparkles />
                  <span>Sign up to join the competition</span>
              </div>
              <button
                  className="status-action"
                  onClick={() => dispatch(toggleLoginOpen(true))}
              >
                  Get Started
              </button>
          </div>
                }

                {userState === "no-guild" &&
          <div className="status-content status-content--no-guild">
              <div className="status-indicator">
                  <FiTrendingUp />
                  <span>Ready to join a guild</span>
              </div>
              {topGuilds && topGuilds.length > 0 &&
              <div className="top-guilds-preview">
                  <span className="preview-label">Top guilds:</span>
                  {topGuilds.slice(0, 3).map((g, idx) =>
                      <span key={g.id} className="guild-chip">
                          #{idx + 1} {g.name}
                      </span>)}
              </div>
              }
              <button
                  className="status-action"
                  onClick={onBrowseGuilds}
              >
                  Browse Guilds
              </button>
          </div>
                }

                {userState === "member" && guild &&
          <div className="status-content status-content--member">
              <div className="guild-status">
                  <div className="guild-identity">
                      {guildIcon?.url
                          ? <img src={guildIcon.url} alt={guild.name} className="guild-icon" />
                          : <div className="guild-icon-placeholder">
                              <FiShield />
                          </div>
                      }
                      <div className="guild-details">
                          <h3>{guild.name}</h3>
                          <div className="guild-metrics">
                              {rank &&
                      <span className="metric rank">
                          Rank {rank}{getRankSuffix(rank)}
                      </span>
                              }
                              <span className="metric">
                                  {points?.toLocaleString() || 0} XP
                              </span>
                              <span className="metric desktop-only">
                                  {guild.memberCount || 0} members
                              </span>
                          </div>
                          <div className="guild-member-count mobile-only">
                              {guild.memberCount || 0} members
                          </div>
                      </div>
                  </div>

                  <div className="guild-actions">
                      <button
                          className="action-btn"
                          onClick={onOpenInvite}
                          title="Invite members"
                      >
                          <IoMdPersonAdd />
                      </button>
                      <button
                          className="action-btn"
                          onClick={onOpenAbout}
                          title="About"
                      >
                          <BiCommentError />
                      </button>
                      <button
                          className="action-btn danger"
                          onClick={onLeaveGuild}
                          title="Leave guild"
                      >
                          <IoExitOutline />
                      </button>
                  </div>
              </div>
          </div>
                }
            </div>

            {/* Info Tooltip */}
            {showInfo &&
        <div className="guild-header-cockpit__info-tooltip">
            <h4>How Guild Competition Works</h4>
            <ul>
                <li>Join a guild to compete with teammates</li>
                <li>Earn XP by completing quests and challenges</li>
                <li>Monthly leaderboards reset on the 1st</li>
                <li>Top guilds earn exclusive rewards</li>
                <li>Tie-breaker: Guild with earliest creation date wins</li>
            </ul>
            <button
                className="close-info"
                onClick={() => setShowInfo(false)}
            >
                Got it
            </button>
        </div>
            }
        </div>
    );
}